import React, { useState, useCallback } from 'react';
import { TagsInput } from '@mantine/core';
import { SubredditTagInputProps, SubredditValidation } from './SubredditTagInput.types';

/**
 * SubredditTagInput Component
 *
 * Input component for managing subreddit lists with validation
 *
 * Features:
 * - Add/remove subreddits
 * - Automatic r/ prefix normalization
 * - Validation for subreddit format
 * - No duplicate subreddits
 * - Maximum tag limit
 * - Case-insensitive handling
 *
 * Validation Rules:
 * - Must start with r/ or be automatically prefixed
 * - Only alphanumeric characters and underscores
 * - 3-21 characters (after r/)
 * - No duplicates
 *
 * Usage:
 * ```tsx
 * <SubredditTagInput
 *   value={subreddits}
 *   onChange={setSubreddits}
 *   label="Subreddits"
 *   maxTags={10}
 * />
 * ```
 */
export const SubredditTagInput: React.FC<SubredditTagInputProps> = ({
  value,
  onChange,
  label = 'Subreddits',
  description = 'Add subreddits to monitor (e.g., AskReddit, news)',
  placeholder = 'Type subreddit name and press Enter',
  error,
  required = false,
  disabled = false,
  maxTags = 50
}): React.ReactElement => {
  const [inputError, setInputError] = useState<string | undefined>(error);

  /**
   * Normalize subreddit name
   * - Remove r/ prefix if present
   * - Convert to lowercase
   * - Trim whitespace
   */
  const normalizeSubreddit = useCallback((name: string): string => {
    let normalized = name.trim().toLowerCase();

    // Remove r/ prefix if present
    if (normalized.startsWith('r/')) {
      normalized = normalized.substring(2);
    }

    return normalized;
  }, []);

  /**
   * Validate subreddit name
   *
   * Rules:
   * - Only alphanumeric characters and underscores
   * - 3-21 characters
   * - No spaces
   */
  const validateSubreddit = useCallback((name: string): SubredditValidation => {
    const normalized = normalizeSubreddit(name);

    // Empty check
    if (!normalized) {
      return { isValid: false, error: 'Subreddit name cannot be empty' };
    }

    // Length check (Reddit's actual limits)
    if (normalized.length < 3) {
      return { isValid: false, error: 'Subreddit name must be at least 3 characters' };
    }

    if (normalized.length > 21) {
      return { isValid: false, error: 'Subreddit name cannot exceed 21 characters' };
    }

    // Format check (only alphanumeric and underscores)
    const validFormat = /^[a-zA-Z0-9_]+$/;
    if (!validFormat.test(normalized)) {
      return {
        isValid: false,
        error: 'Subreddit name can only contain letters, numbers, and underscores'
      };
    }

    // Check for duplicates
    const normalizedValues = value.map((v) => normalizeSubreddit(v));
    if (normalizedValues.includes(normalized)) {
      return { isValid: false, error: 'This subreddit is already added' };
    }

    return { isValid: true };
  }, [normalizeSubreddit, value]);

  /**
   * Handle tag addition
   */
  const handleChange = useCallback(
    (newValues: string[]): void => {
      setInputError(undefined);

      // Check max tags
      if (maxTags && newValues.length > maxTags) {
        setInputError(`Maximum ${maxTags} subreddits allowed`);
        return;
      }

      // If a new tag was added, validate and normalize it
      if (newValues.length > value.length) {
        const newTag = newValues[newValues.length - 1];
        if (!newTag) {return;}

        const validation = validateSubreddit(newTag);
        if (!validation.isValid) {
          setInputError(validation.error);
          return;
        }

        // Normalize all values
        const normalizedValues = newValues.map((v) => normalizeSubreddit(v));
        onChange(normalizedValues);
      } else {
        // Tag was removed
        const normalizedValues = newValues.map((v) => normalizeSubreddit(v));
        onChange(normalizedValues);
      }
    },
    [value, onChange, validateSubreddit, normalizeSubreddit, maxTags]
  );

  /**
   * Display value with r/ prefix for UI
   */
  const displayValue = value.map((v) => `r/${normalizeSubreddit(v)}`);

  return (
    <TagsInput
      label={label}
      description={description}
      placeholder={placeholder}
      value={displayValue}
      onChange={(newDisplayValues) => {
        // Remove r/ prefix from display values before passing to handler
        const cleanValues = newDisplayValues.map((v) => {
          if (v.startsWith('r/')) {
            return v.substring(2);
          }
          return v;
        });
        handleChange(cleanValues);
      }}
      error={inputError || error}
      required={required}
      disabled={disabled}
      splitChars={[',', ' ', '\n']}
      clearable
      maxTags={maxTags}
    />
  );
};
